import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CommonStyles.css';
import './Products.css';
import './ProductSearchStyles.css';
import './AddSellStyles.css';
import apiService from '../../services/api';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';

const StockTransfers = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('list_transfers');

  // Shared State
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // List State
  const [transfers, setTransfers] = useState([]);
  const [perPage, setPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [openActionId, setOpenActionId] = useState(null);

  // Add Transfer State
  const [locations, setLocations] = useState([]);

  const getCurrentDateTime = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(now - offset)).toISOString().slice(0, 16);
    return localISOTime;
  };

  const [formData, setFormData] = useState({
    date: getCurrentDateTime(),
    referenceNo: '',
    status: 'pending',
    locationFrom: '',
    locationTo: '',
    shippingCharges: 0,
    note: ''
  });

  const [items, setItems] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await apiService.getBusinessLocations();
      setLocations(response.data || response || []);
    } catch (error) {
      console.error('Failed to fetch locations', error);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (productSearchTerm.length > 1) {
        searchProducts(productSearchTerm);
      } else {
        setProductSearchResults([]);
        setShowProductDropdown(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [productSearchTerm]);

  const searchProducts = async (term) => {
    try {
      const response = await apiService.getProducts({ search: term });
      const results = response.data || response || [];
      setProductSearchResults(results);
      setShowProductDropdown(results.length > 0);
    } catch (error) {
      console.error('Failed to search products', error);
    }
  };

  const handleAddProduct = (product) => {
    const existingItemIndex = items.findIndex(item => item.id === product.id);

    if (existingItemIndex >= 0) {
      const newItems = [...items];
      newItems[existingItemIndex].quantity += 1;
      newItems[existingItemIndex].subtotal = newItems[existingItemIndex].quantity * newItems[existingItemIndex].unitPrice;
      setItems(newItems);
    } else {
      const unitPrice = parseFloat(
        product.sell_price_inc_tax || 
        product.default_sell_price || 
        product.purchase_price || 
        product.unit_price || 
        product.product_variations?.[0]?.variations?.[0]?.sell_price_inc_tax ||
        product.product_variations?.[0]?.variations?.[0]?.default_sell_price ||
        product.product_variations?.[0]?.variations?.[0]?.default_purchase_price ||
        0
      );
      setItems([...items, {
        id: product.id,
        name: product.name,
        sku: product.sku,
        quantity: 1,
        unitPrice: unitPrice,
        subtotal: unitPrice
      }]);
    }
    setProductSearchTerm('');
    setShowProductDropdown(false);
  };

  const handleQuantityChange = (index, newQty) => {
    const qty = parseFloat(newQty) || 0;
    const newItems = [...items];
    newItems[index].quantity = qty;
    newItems[index].subtotal = qty * newItems[index].unitPrice;
    setItems(newItems);
  };

  const handleRemoveItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const totalItemAmount = items.reduce((sum, item) => sum + item.subtotal, 0);
  const totalAmount = totalItemAmount + (parseFloat(formData.shippingCharges) || 0);

  const handleExport = (format) => {
    if (!transfers || transfers.length === 0) {
      alert('No data to export');
      setShowExportDropdown(false);
      return;
    }

    const headers = ['Date', 'Reference No', 'Location (From)', 'Location (To)', 'Status', 'Shipping Charges', 'Total Amount', 'Additional Notes'];
    const rows = transfers.map(t => [
      t.date || '',
      t.ref_no || '',
      t.location_from || '',
      t.location_to || '',
      t.status || '',
      t.shipping_charges || '0.00',
      t.total_amount || '0.00',
      t.notes || ''
    ]);

    const title = 'Stock Transfers List';
    const filename = 'stock_transfers';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const handlePrint = () => { printPage(); setShowExportDropdown(false); };
  const handleColumnVisibility = () => { alert('Column visibility settings'); setShowExportDropdown(false); };

  const handleAddClick = () => {
    setActiveTab('add_transfer');
  };

  const handleCancelAdd = () => {
    setActiveTab('list_transfers');
    setItems([]);
    setFormData({
      date: getCurrentDateTime(),
      referenceNo: '',
      status: 'pending',
      locationFrom: '',
      locationTo: '',
      shippingCharges: 0,
      note: ''
    });
  };

  // RENDER ADD FORM
  if (activeTab === 'add_transfer') {
    return (
      <div className="products-container">
        <div className="main-card">
          <div className="product-tabs">
            <button
              onClick={handleCancelAdd}
              style={{
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '0.5rem 0.75rem',
                cursor: 'pointer',
                fontSize: '1.2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '40px',
                height: '40px'
              }}
            >
              ←
            </button>
            <div className="product-tab active" style={{ marginLeft: 'auto' }}>
              <i className="fa fa-plus"></i> Add Stock Transfer
            </div>
          </div>

          <form className="transfer-form" style={{ padding: '1.5rem' }}>
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              <div className="form-group">
                <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Date:*</label>
                <input
                  type="datetime-local"
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none', fontSize: '0.875rem' }}
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Reference No:</label>
                <input
                  type="text"
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none', fontSize: '0.875rem' }}
                  placeholder="Leave empty to auto-generate"
                  value={formData.referenceNo}
                  onChange={(e) => setFormData({ ...formData, referenceNo: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Location (From):*</label>
                <select
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none', fontSize: '0.875rem' }}
                  value={formData.locationFrom}
                  onChange={(e) => setFormData({ ...formData, locationFrom: e.target.value })}
                >
                  <option value="">Select Location</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name} ({loc.location_id})</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Location (To):*</label>
                <select
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none', fontSize: '0.875rem' }}
                  value={formData.locationTo}
                  onChange={(e) => setFormData({ ...formData, locationTo: e.target.value })}
                >
                  <option value="">Select Location</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name} ({loc.location_id})</option>
                  ))}
                </select>
              </div>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '1.5rem 0' }} />

            <div className="product-search-section" style={{ position: 'relative', marginBottom: '1.5rem' }}>
              <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Enter Product name / SKU / Scan bar code</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search products for stock transfer"
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  onFocus={() => productSearchTerm.length > 1 && setShowProductDropdown(true)}
                  autoComplete="off"
                  style={{ width: '100%', padding: '0.7rem 1rem', paddingLeft: '2.5rem', border: '1px solid #3b82f6', borderRadius: '0.5rem', fontSize: '0.875rem', outline: 'none', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)' }}
                />
                <i className="fa fa-search" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#3b82f6', fontSize: '1rem' }}></i>
              </div>
              {showProductDropdown && productSearchResults.length > 0 && (
                <div className="product-dropdown" style={{ borderRadius: '0.5rem', border: '1px solid #e2e8f0', marginTop: '4px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', position: 'absolute', width: '100%', zIndex: 100, background: 'white', maxHeight: '300px', overflowY: 'auto' }}>
                  {productSearchResults.map(product => (
                    <div
                      key={product.id}
                      className="product-dropdown-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAddProduct(product);
                      }}
                      style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.2s' }}
                    >
                      <div className="product-name" style={{ fontWeight: '600', color: '#1e293b' }}>{product.name} <span style={{ fontWeight: '400', color: '#64748b', fontSize: '0.85rem' }}>({product.sku})</span></div>
                    </div>
                  ))}
                </div>
              )}
              {showProductDropdown && productSearchResults.length === 0 && productSearchTerm.length > 1 && (
                <div className="product-dropdown" style={{ borderRadius: '0.5rem', border: '1px solid #e2e8f0', marginTop: '4px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', position: 'absolute', width: '100%', zIndex: 100, background: 'white' }}>
                  <div style={{ padding: '0.75rem 1rem', color: '#64748b' }}>No products found</div>
                </div>
              )}
            </div>

            <div className="product-table-container" style={{ marginBottom: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '0.75rem', background: '#fff', overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.8125rem', tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '0.5rem 0.6rem', color: '#475569', fontWeight: '700', width: '55%' }}>PRODUCT</th>
                    <th style={{ padding: '0.5rem 0.6rem', color: '#475569', fontWeight: '700', width: '10%' }}>QUANTITY</th>
                    <th style={{ padding: '0.5rem 0.6rem', color: '#475569', fontWeight: '700', width: '12%' }}>UNIT PRICE</th>
                    <th style={{ padding: '0.5rem 0.6rem', color: '#475569', fontWeight: '700', width: '13%' }}>SUBTOTAL</th>
                    <th style={{ padding: '0.5rem 0.6rem', color: '#475569', fontWeight: '700', textAlign: 'center', width: '10%' }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>No products added. Start searching to add products to the transfer.</td>
                    </tr>
                  ) : (
                    items.map((item, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '0.5rem 0.6rem' }}>
                          <div style={{ fontWeight: '600', color: '#1e293b' }}>{item.name}</div>
                          {item.sku && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>SKU: {item.sku}</div>}
                        </td>
                        <td style={{ padding: '0.5rem 0.6rem' }}>
                          <input
                            type="number"
                            min="0.1"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => handleQuantityChange(index, e.target.value)}
                            style={{ width: '100%', maxWidth: '70px', padding: '0.4rem', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none', fontSize: '0.8125rem' }}
                          />
                        </td>
                        <td style={{ padding: '0.5rem 0.6rem', fontWeight: '700', color: '#1e293b' }}>${item.unitPrice.toFixed(2)}</td>
                        <td style={{ padding: '0.5rem 0.6rem', fontWeight: '700', color: '#1e293b' }}>${item.subtotal.toFixed(2)}</td>
                        <td style={{ padding: '0.5rem 0.6rem', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            style={{ color: '#ef4444', background: '#fee2e2', border: 'none', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="form-summary-section" style={{ borderTop: '2px solid #f1f5f9', paddingTop: '1.5rem', display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '2rem' }}>
              <div className="summary-left" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Shipping Charges:</label>
                  <input
                    type="number"
                    value={formData.shippingCharges}
                    onChange={(e) => setFormData({ ...formData, shippingCharges: e.target.value })}
                    style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none', fontSize: '0.875rem' }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Additional Notes:</label>
                  <textarea
                    value={formData.note}
                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                    rows="2"
                    placeholder="Additional notes for stock transfer"
                    style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none', resize: 'vertical', fontSize: '0.875rem' }}
                  ></textarea>
                </div>
              </div>
              <div className="summary-right" style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '1rem', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                  <span style={{ color: '#64748b', fontWeight: '500' }}>Total Items:</span>
                  <span style={{ fontWeight: '700' }}>{items.length}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.25rem', borderTop: '2px solid #e2e8f0', paddingTop: '1rem' }}>
                  <span style={{ fontWeight: '800', color: '#1e293b' }}>Total Amount:</span>
                  <span style={{ fontWeight: '900', color: '#2563eb' }}>${totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="form-actions" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
              <button type="button" onClick={handleCancelAdd} style={{ padding: '0.7rem 2rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: '700', color: '#475569', cursor: 'pointer', fontSize: '0.875rem' }}>Cancel</button>
              <button type="button" style={{ padding: '0.7rem 2.5rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)', fontSize: '0.875rem' }}>Save</button>
            </div>
          </form>
        </div>
      </div >
    );
  }

  // RENDER LIST VIEW
  return (
    <div className="products-container">
      <div className="main-card">
        <div className="product-tabs">
          <div
            className={`product-tab ${activeTab === 'list_transfers' ? 'active' : ''}`}
            onClick={() => setActiveTab('list_transfers')}
          >
            <i className="fa fa-list"></i> List Stock Transfers
          </div>

        </div>

        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button className="filter-toggle-btn" onClick={() => setShowFilters(true)} title="Open Filters">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={handleAddClick}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        <div className="table-content-card">
          <div className="products-table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Date</th>
                  <th>Reference No</th>
                  <th>Location (From)</th>
                  <th>Location (To)</th>
                  <th>Status</th>
                  <th>Shipping Charges</th>
                  <th>Total Amount</th>
                  <th>Additional Notes</th>
                </tr>
              </thead>
              <tbody>
                {transfers.length === 0 ? (
                  <tr><td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>No stock transfers found.</td></tr>
                ) : (
                  transfers.map(transfer => (
                    <tr key={transfer.id}>
                      <td>...</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
            <div>Showing 0 to 0 of 0 entries</div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="pagination-btn" disabled>Previous</button>
              <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>Page 1 of 1</span>
              <button className="pagination-btn" disabled>Next</button>
            </div>
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
            <div className="modal-header">
              <h2><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>Filters</h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div className="filter-row">
                <div className="filter-group"><label>Status:</label><select><option value="all">All Status</option></select></div>
                <div className="filter-group"><label>Location (From):</label><select><option value="all">All</option></select></div>
                <div className="filter-group"><label>Location (To):</label><select><option value="all">All</option></select></div>
                <div className="filter-group"><label>Date Range:</label><input type="date" /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={() => setShowFilters(false)}>Close</button>
              <button className="modal-btn primary-btn">Apply Filters</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockTransfers;
